import React, { useState, useMemo } from "react";
import classNames from "classnames";
import { Wrapper, Drawer } from "@/components";
import { i18n, thumbnail7n, priceFormat } from "@/utils";
import { Shop } from "@/sunzi";
import Icon from "./icon";
import styles from "./style.less";
import CountDown from "./count-down";
import { relatedRecordProductProps, SizeVariant } from "./mini-me";

interface RelatedRecordProductProps {
  /** 商店信息 */
  shop: Shop;
  /** 是否重新选择尺寸 */
  isResize: boolean;
  /** 是否是追销 ar 动画 */
  isArAnimation: boolean;
  /** 尺寸变体 */
  sizeVariant: SizeVariant[];
  /** 增值产品 */
  relatedRecordProduct: relatedRecordProductProps;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm: (addStatus: boolean, currentSize?: string) => void;
}

const RelatedRecordProduct: React.FC<RelatedRecordProductProps> = ({
  shop,
  isResize,
  isArAnimation,
  sizeVariant,
  relatedRecordProduct,
  onClose,
  onConfirm,
}) => {
  /** 修改尺寸显隐 */
  const [sizeVisible, setSizeVisible] = useState<boolean>(false);
  /** 当前改的尺寸 */
  const [currentSize, setCurrentSize] = useState<string>(sizeVariant[0].value);

  /**
   * @description 修改尺寸触发回调
   */
  const handleChangeSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentSize(e.target.value);
  };

  const discount = useMemo(() => {
    const ratio = relatedRecordProduct.price / relatedRecordProduct.originPrice;
    if (shop.language === "tw") {
      // tw站特殊处理成x折的形式
      let discount = (ratio * 10).toFixed(1);
      if (discount[discount.length - 1] === "0") {
        discount = discount.split(".")[0];
      }
      return discount;
    } else return ((1 - ratio) * 100).toFixed(2);
  }, [relatedRecordProduct.price, relatedRecordProduct.originPrice]);

  return (
    <Wrapper className={styles.relatedRecordProduct}>
      <Drawer mask className={styles.relatedRecordProductDrawer}>
        {sizeVisible ? (
          <div className={classNames(styles.drawerContent, styles.size)}>
            <div
              className={styles.previous}
              onClick={() => setSizeVisible(false)}
            >
              <Icon type="arrow-left" color="#000" size={30} />
            </div>
            <div className={styles.selectWrapper}>
              <p>{i18n.format("modules.mini.me.change.size")}</p>
              <div className={styles.select}>
                <select onChange={handleChangeSize}>
                  {sizeVariant.map((item, index) => (
                    <option value={item.value} key={index}>
                      {item.value}
                    </option>
                  ))}
                </select>
                <Icon
                  className={styles.arrowDown}
                  type="arrow-down"
                  size={24}
                  color={"#000"}
                />
              </div>
            </div>
            <div className={styles.buttonGroup}>
              <div
                className={styles.confirm}
                onClick={() => onConfirm(true, currentSize)}
              >
                <div className={styles.shopCart}>
                  <Icon type="shop-cart" size={30} color={"#ffffff"} />
                </div>
                <span className={styles.confirmText}>
                  {i18n.format("modules.mini.me.sure.save.now")}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.drawerContent}>
            <div className={styles.imageWrapper}>
              <div
                className={styles.image}
                style={{
                  backgroundImage: `url(${thumbnail7n(
                    relatedRecordProduct.thumbnail,
                    400
                  )})`,
                }}
              />
              <div className={styles.offer}>
                {i18n.format("modules.mini.me.off", `${discount}`)}
              </div>
            </div>
            <div className={styles.previous} onClick={onClose}>
              <Icon type="arrow-left" color="#000" size={30} />
            </div>
            <div className={styles.countdown}>
              <CountDown minute={10} />
            </div>
            {/* tip */}
            <div className={styles.tipWrapper}>
              <span className={styles.tip1}>
                {i18n.format("modules.mini.me.limit.time")}
              </span>
              <span className={styles.tip2}>
                {isArAnimation ? (
                  <>
                    {i18n.format("modules.mini.me.get.ar")}
                    <span className={styles.price1}>
                      {priceFormat(
                        shop.currencySymbol,
                        relatedRecordProduct.originPrice
                      )}
                    </span>
                    <span className={styles.price2}>
                      {priceFormat(
                        shop.currencySymbol,
                        relatedRecordProduct.price
                      )}
                      !
                    </span>
                  </>
                ) : (
                  <>
                    {i18n.format("modules.mini.me.only")}
                    <span className={styles.price1}>
                      {priceFormat(
                        shop.currencySymbol,
                        relatedRecordProduct.originPrice
                      )}
                    </span>
                    <span className={styles.price2}>
                      {priceFormat(
                        shop.currencySymbol,
                        relatedRecordProduct.price
                      )}
                    </span>
                    {i18n.format("modules.mini.me.get.recorder")}
                    {isResize &&
                      `,${i18n.format("modules.mini.me.get.recorder.tip")}`}
                  </>
                )}
              </span>
            </div>
            <div className={styles.buttonGroup}>
              <div
                className={styles.confirm}
                onClick={() => {
                  if (isResize) setSizeVisible(true);
                  else onConfirm(true);
                }}
              >
                <div className={styles.shopCart}>
                  {isResize ? (
                    <Icon type="transform" size={30} color={"#ffffff"} />
                  ) : (
                    <Icon type="shop-cart" size={30} color={"#ffffff"} />
                  )}
                </div>
                <span className={styles.confirmText}>
                  {isResize
                    ? i18n.format("modules.mini.me.ok.change.size")
                    : i18n.format("modules.mini.me.sure.save.now")}
                </span>
              </div>
              <div className={styles.divider}>
                <span>{i18n.format("modules.mini.me.or")}</span>
              </div>
              <div className={styles.denied} onClick={() => onConfirm(false)}>
                <span>{i18n.format("modules.mini.me.no.thanks")}</span>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </Wrapper>
  );
};

export default RelatedRecordProduct;
